import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { format } from 'date-fns';
import * as Steps from './steps/index';
import { HeaderProgressTracker } from '../Common/HeaderStepper/HeaderProgressTracker';
import { getSessionStorageItem, SESSION_STORAGE_COVR_FULLUW_KEY, SESSION_STORAGE_ACCOUNT_KEY, getBasicInfo, setBasicInfo, SESSION_STORAGE_BASIC_INFO_KEY, SESSION_STORAGE_UTM_DATA, storeConsumerApplicationId, deleteConsumerApplicationId, SESSION_STORAGE_SSO_ACCESS_TOKEN_KEY } from '../../../sessionStorage';
import TermData from '../../Helpers/Data/Products/Term/TermData';
import { saveSessionEvent } from "../../../session";
import GetProductTypeEnum from '../../../types/productType';
import CheckSSOLogin from '../Common/CheckSSOLogin';
import TobaccoUsage from "../Common/Helpers/TobaccoUsage";
import { QuoteProductID } from "../Common/QuoteProductID";
import { GetHandoffPath } from "../Common/GetHandoffPath";
import GetProductDetail from "../../Common/GetProductDetail";
import '../Quoter.scss';
import './MCT.scss';

export default function MCT() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => { saveSessionEvent(); }, [location]);

  const path = location ? location.pathname.split("/").pop() : null;

  if (path === 'ns') {
    //clear product session
    sessionStorage.removeItem(SESSION_STORAGE_COVR_FULLUW_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_BASIC_INFO_KEY);
  }

  // 27514 - Since E*Trade is unable to change the URL on their side, redirect to their landing page when they hit /quote/term-life-insurance/multi-carrier-term
  useEffect(() => {
    const account = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);
    let productDetail = GetProductDetail(['/landing/term', '/quote/term-life-insurance/multi-carrier-term']);
    if (productDetail === undefined) {
      productDetail = GetProductDetail(['/landing/term-insurance', '/term-insurance']);
      const redirectPath = productDetail === undefined ? '/' : '/landing/term-insurance';
      navigate(redirectPath);
    }
    if (location.pathname === '/quote/term-life-insurance/multi-carrier-term' && account.account.accountName.toLowerCase().indexOf("etrade") !== -1) {
      navigate('/landing/etrade-term');
    }
  }, []);

  const sessionStorageFormState = getSessionStorageItem(SESSION_STORAGE_COVR_FULLUW_KEY);
  if (sessionStorageFormState && sessionStorageFormState.pages) {
    const basicInfo = getBasicInfo();
    const updateBasicInfo = sessionStorageFormState.pages.aboutMe;
    updateBasicInfo.firstName = basicInfo.firstName || '';
    updateBasicInfo.lastName = basicInfo.lastName || '';
    updateBasicInfo.email = basicInfo.email || '';
    if (basicInfo.faceAmount) {
      updateBasicInfo.faceAmount = basicInfo.faceAmount;
    }
    if (basicInfo.termLength) {
      updateBasicInfo.termLength = basicInfo.termLength;
    }
    setBasicInfo(updateBasicInfo);
  }

  const mctProductSettings = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).accountProducts.find(accPrd => accPrd.productTypeId === GetProductTypeEnum().FullUW);
  const prefill = location.search && location.search.toLowerCase().includes('prefill');
  const basicInfo = getBasicInfo();
  const url = window.location.href;
  const isHasBasicInfo = basicInfo.gender && basicInfo.birthdate && basicInfo.state && basicInfo.tobacco && basicInfo.height && basicInfo.weight;

  if ((url.includes("pre-quote") && !isHasBasicInfo)) {
    navigate('/landing/term', { replace: true });
  }

  const newFormState = {
    currentStep: 1,
    pages: {
      aboutMe: Steps.AboutMe.DefaultFormState(prefill, basicInfo),
      preQuote: Steps.PreQuote.DefaultFormState(prefill, basicInfo),
      myHealthProfile: Steps.MyHealthProfile.DefaultFormState(prefill, basicInfo),
      background: Steps.Background.DefaultFormState(prefill, basicInfo),
      tobaccoUse: Steps.TobaccoUse.DefaultFormState(prefill, basicInfo),
      quoter: Steps.Quoter.DefaultFormState(prefill, basicInfo),
      appointment: Steps.Appointment.DefaultFormState(prefill, basicInfo),
      adventures: Steps.Adventures.DefaultFormState(prefill, basicInfo),
      drivingRecord: Steps.DrivingRecord.DefaultFormState(),
      familyHistory: Steps.FamilyHistory.DefaultFormState(prefill, basicInfo),
      medicalDetails: Steps.MedicalDetails.DefaultFormState(prefill),
      medicalHistory: Steps.MedicalHistory.DefaultFormState(prefill, basicInfo),
      alcoholDrugAbuse: Steps.AlcoholDrugAbuse.DefaultFormState(prefill, basicInfo),
      insuranceRequest: Steps.InsuranceRequest.DefaultFormState(prefill, basicInfo),
    },
  };

  const initialFormState = sessionStorageFormState || newFormState;

  const [formState, setFormState] = useState(initialFormState);
  const [quoteLogId, setQuoteLogId] = useState(null);
  const termData = TermData();
  const [multiTermResult, setMultiTermResult] = useState(false);
  const [isSSOAuthenticated, setIsSSOAuthenticated] = useState(false);

  // When coming from the landing page form, skip to step 2
  useEffect(() => {
    if (location.search.includes('landing') && formState.currentStep === 1) {
      setFormState({ ...formState, currentStep: 2 });
    }
    if (!isSSOAuthenticated) {
      if (getSessionStorageItem(SESSION_STORAGE_SSO_ACCESS_TOKEN_KEY)) {
        setIsSSOAuthenticated(true);
      }
    }
  }, []);

  // update the formState when the url path changes the step
  useEffect(() => {
    if (!path) {
      return;
    }

    const nextStep = steps.find(s => s.path === path);

    // prevent submitted app alterations
    if (!sessionStorageFormState && formState.currentStep === steps.length) {

      // if they are backing or directly going to another step, send them to product root
      if (formState.currentStep !== nextStep.step) {
        navigate('/quote/term-life-insurance/multi-carrier-term');
      }
    }

    if (!nextStep) {
      return;
    }
    const newFormState = { ...formState, currentStep: nextStep.step };
    setFormState(newFormState);
  }, [path]);

  const showMedicalDetailsPage = formState.pages.myHealthProfile.personalHistoryMedical && (formState.pages.medicalHistory.hypertensionOrHighBloodPressure || formState.pages.medicalHistory.highCholesterol || formState.pages.medicalHistory.diabetes);

  const showDrivingRecordPage = formState.pages.background.TwoMovingViolations || formState.pages.background.recklessDrivingOrDUI;

  const onNext = () => {
    let nextStep = steps.find(s => s.step === formState.currentStep + 1);
    const tobaccoUsage = TobaccoUsage(formState.pages.aboutMe.tobacco);

    if (nextStep.path === "tobacco-use" && tobaccoUsage === "false") {
      nextStep = steps.find(s => s.step === nextStep.step + 1);
    }

    if (nextStep.path === 'medical-history' && !formState.pages.myHealthProfile.personalHistoryMedical) {
      nextStep = steps.find(s => s.step === nextStep.step + 1);
    }

    if (nextStep.path === 'medical-details' && showMedicalDetailsPage === false) {
      nextStep = steps.find(s => s.step === nextStep.step + 1);
    }

    if (nextStep.path === 'family-history' && !formState.pages.myHealthProfile.familyHistory) {
      nextStep = steps.find(s => s.step === nextStep.step + 1);
    }

    if (nextStep.path === 'alcohol-drug-abuse' && !formState.pages.myHealthProfile.treatedAlcoholSubstanceAbuse) {
      nextStep = steps.find((s) => s.step === nextStep.step + 1);
    }

    if (nextStep.path === 'driving-record' && showDrivingRecordPage === false) {
      nextStep = steps.find((s) => s.step === nextStep.step + 1);
    }

    if (nextStep.path === 'adventures' && formState.pages.background.hazardousActivities === false) {
      nextStep = steps.find((s) => s.step === nextStep.step + 1);
    }

    if (!nextStep) {
      throw new Error("onNext did not find a next step");
    }

    const newFormState = { ...formState, currentStep: nextStep.step };

    setFormState(newFormState);
    sessionStorage.setItem(SESSION_STORAGE_COVR_FULLUW_KEY, JSON.stringify(newFormState));

    const covrServicesUrl = window.__ENV__.REACT_APP_COVR_SERVICES_URL;

    if (isSSOAuthenticated && nextStep.path !== 'thank-you') {
      fetch(`${covrServicesUrl}/api/ConsumerApplication/SSOPost`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          'SessionId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).sessionId,
          'X-Consumer-Application-ID': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).consumerApplicationId,
          'Authorization': 'Bearer ' + getSessionStorageItem(SESSION_STORAGE_SSO_ACCESS_TOKEN_KEY),
        },
        body: JSON.stringify(newFormState),
      }).then(storeConsumerApplicationId);
    }

    navigate(`/quote/term-life-insurance/multi-carrier-term/${nextStep.path}`);
    window.scroll(0, 0);
  };

  const onBack = () => {
    let backStep = steps.find(s => s.step === formState.currentStep - 1);
    const tobaccoUsage = TobaccoUsage(formState.pages.aboutMe.tobacco);
    if (backStep.path === 'adventures' && formState.pages.background.hazardousActivities === false) {
      backStep = steps.find((s) => s.step === backStep.step - 1);
    }

    if (backStep.path === 'driving-record' && showDrivingRecordPage === false) {
      backStep = steps.find((s) => s.step === backStep.step - 1);
    }

    if (backStep.path === 'alcohol-drug-abuse' && !formState.pages.myHealthProfile.treatedAlcoholSubstanceAbuse) {
      backStep = steps.find((s) => s.step === backStep.step - 1);
    }

    if (backStep.path === 'family-history' && !formState.pages.myHealthProfile.familyHistory) {
      backStep = steps.find(s => s.step === backStep.step - 1);
    }

    if (backStep.path === 'medical-details' && showMedicalDetailsPage === false) {
      backStep = steps.find(s => s.step === backStep.step - 1);
    }

    if (backStep.path === 'medical-history' && !formState.pages.myHealthProfile.personalHistoryMedical) {
      backStep = steps.find(s => s.step === backStep.step - 1);
    }

    if (backStep.path === "tobacco-use" && tobaccoUsage === "false") {
      backStep = steps.find(s => s.step === backStep.step - 1);
    }

    let redirect = '';
    if (!backStep) {
      redirect = 'basic-info';
    } else {

      const newFormState = { ...formState, currentStep: backStep.step };

      setFormState(newFormState);
      sessionStorage.setItem(SESSION_STORAGE_COVR_FULLUW_KEY, JSON.stringify(newFormState));

      redirect = `multi-carrier-term/${backStep.path}`;

    }
    navigate(`/quote/term-life-insurance/${redirect}`);
    window.scroll(0, 0);
  };

  const onNextLga = () => {
    navigate(`/quote/term-life-insurance/multi-carrier-term/lga`);
    window.scroll(0, 0);
  };

  const onFieldChange = (step, field, value) => {
    setFormState({ ...formState, pages: { ...formState.pages, [step]: { ...formState.pages[step], [field]: value } } });
  };

  const logFinalDigitalQuote = (email, firstName, lastName, quoter, oneClickTypeId, referralId = null, phone = '') => {
    const account = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);
    const isSelectedQuoteCarrier = quoter.selectedQuote.carrierName !== "" && quoter.selectedQuote.carrierName !== null;
    const utmData = getSessionStorageItem(SESSION_STORAGE_UTM_DATA);

    return new Promise(resolve => {
      //save quote log for bms
      //keep in mind that this needs to match 1click quote log format
      let mctQuoteData = {
        CoverageAmount: quoter.coverageAmount,
        CoverageLength: quoter.term,
        State: basicInfo.state,
        BirthDate: format(new Date(basicInfo.birthdate), 'yyyy-MM-dd'),
        HealthClass: quoter.healthClass,
        Gender: basicInfo.gender,
        Tobacco: TobaccoUsage(basicInfo.tobacco),
        Email: email,
        FirstName: firstName,
        LastName: lastName,
        selectedProductID: isSelectedQuoteCarrier && quoter.selectedQuote.carrierName.toLowerCase().includes("lga") ? quoter.selectedQuote.productID : "",
      };
      //save quote log for bms
      //keep in mind that this needs to match 1click quote log format
      const requestString = JSON.stringify(mctQuoteData);

      let jsonResponse;

      // set custom quote id in response if present
      if (referralId) {
        const response = { ...quoter.selectedQuote };
        response.PartnerQuoteId = referralId;
        response.UTMs = utmData || "";

        jsonResponse = JSON.stringify(response);
      }
      else {
        quoter.selectedQuote.UTMs = utmData || "";
        jsonResponse = JSON.stringify(quoter.selectedQuote);
      }

      const pathName = GetHandoffPath(quoter.selectedQuote.productID);      
      saveSessionEvent(true, pathName);
      //clear product session
      sessionStorage.removeItem(SESSION_STORAGE_COVR_FULLUW_KEY);
      sessionStorage.removeItem(SESSION_STORAGE_BASIC_INFO_KEY);

      const accessToken = getSessionStorageItem(SESSION_STORAGE_SSO_ACCESS_TOKEN_KEY);
      const covrServicesUrl = window.__ENV__.REACT_APP_COVR_SERVICES_URL;

      fetch(`${covrServicesUrl}/api/QuoteLog/ArchiveQuoteLog`, {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
          'SessionId': account.sessionId,
          'X-Consumer-Application-ID': account.consumerApplicationId,
          'Authorization': accessToken ? 'Bearer ' + accessToken : null,
        },
        body: JSON.stringify({
          QuoteLogId: quoter.selectedQuote.quoteLogId,
          OneClickTypeID: isSelectedQuoteCarrier && quoter.selectedQuote.carrierName.toLowerCase().includes("lga") ? 52 : oneClickTypeId, //digital oneclick type
          Request: requestString,
          Response: jsonResponse,
          ApplicationID: account.sessionId,
          OneClickAccountID: account.account.oneClickAccountId,
          SessionID: account.sessionId,
          ConsumerAccountId: account.account.accountId,
          PartnerQuoteId: referralId,
        }),
      })
      .then(deleteConsumerApplicationId)
      .then(() => { resolve(); });
    });
  };

  const resetHealthProfileValues = (step) => {
    setFormState({
      ...formState,
      pages: {
        ...formState.pages,
        [step]: {
          ...formState.pages[step],
          personalHistoryMedical: false,
          hospitalized: false,
          familyHistory: false,
          treatedAlcoholSubstanceAbuse: false,
        },
      },
    });
  };

  const calculatePercentage = (totalStep, step, categoryStep, currentCateTotalStep) => {
    const stepPer = ((step - 1) / totalStep * 100);
    const cateCount = currentCateTotalStep === 1 ? 1 : (((categoryStep - 0.5) / currentCateTotalStep) * (100 / totalStep));
    return stepPer + cateCount;
  };

  // not able to set multiple field value change at one time, for work none checkbox need to reset vales
  const resetPersonalizeOptions = (step) => {
    setFormState({
      ...formState, pages:
      {
        ...formState.pages,
        [step]: {
          ...formState.pages[step],
          TwoMovingViolations: false,
          recklessDrivingOrDUI: false,
          chargedFelony: false,
          bankruptcyWithinLast5Years: false,
          receivingSSOrDisabled: false,
          hazardousActivities: false,
        },
      },
    });
  };

  const steps = [
    {
      step: 1,
      path: 'about-me',
      progressTitle: 'Basics',
      component: (
        <Steps.AboutMe
          formState={formState.pages.aboutMe}
          basicInfo={basicInfo}
          ageSpecs={{ min: 18, max: 70 }}
          onFieldChange={(field, value) => onFieldChange('aboutMe', field, value)}
          setFormState={(coverageAmount, termLength, healthClass) =>
            setFormState({
              ...formState,
              pages: { ...formState.pages, quoter: { ...formState.pages.quoter, coverageAmount: coverageAmount, term: termLength, healthClass: healthClass } },
            })
          }
          onNext={onNext}
          onBack={() => { }}
          coverageAmountSpecs={termData.specs.coverageAmount}
        />
      ),
      categoryNumber: 1,
      categoryStep: 1,
      showContainer: true,
    },
    {
      step: 2,
      path: 'pre-quote',
      progressTitle: 'Quick Quote',
      title: 'Let’s customize your coverage',
      component: (
        <Steps.PreQuote
          formState={formState.pages.preQuote}
          basicInfo={formState.pages.aboutMe}
          quoter={formState.pages.quoter}
          fullFormState={formState}
          onFieldChange={(field, value) => onFieldChange('preQuote', field, value)}
          setFormState={(coverageAmount, termLength, healthClass) =>
            setFormState({
              ...formState,
              pages: { ...formState.pages, quoter: { ...formState.pages.quoter, coverageAmount: coverageAmount, term: termLength, healthClass: healthClass } },
            })
          }
          onNext={onNext}
          onBack={onBack}
          onMultiTerm={(multi) => setMultiTermResult(multi)}
          coverageAmountSpecs={termData.specs.coverageAmount}
          onMultiFieldChange={(fs) => {
            setFormState(fs);
            sessionStorage.setItem(SESSION_STORAGE_COVR_FULLUW_KEY, JSON.stringify(fs));
          }}
        />
      ),
      categoryNumber: 2,
      categoryStep: 1,
      showContainer: false,

    },
    {
      step: 3,
      path: 'my-health-profile',
      progressTitle: 'Personalize',
      title: 'Determining your price and policy options',
      component: (
        <Steps.MyHealthProfile
          formState={formState.pages.myHealthProfile}
          basicInfo={basicInfo}
          onFieldChange={(field, value) => onFieldChange('myHealthProfile', field, value)}
          setFormState={(coverageAmount, termLength, healthClass) =>
            setFormState({
              ...formState,
              pages: { ...formState.pages, quoter: { ...formState.pages.quoter, coverageAmount: coverageAmount, term: termLength, healthClass: healthClass } },
            })
          }
          onNext={onNext}
          onBack={onBack}
          resetHealthProfileValues={() => resetHealthProfileValues('myHealthProfile')}
          coverageAmountSpecs={termData.specs.coverageAmount}
        />
      ),
      categoryNumber: 3,
      categoryStep: 1,
      showContainer: true,
    },
    {
      step: 4,
      path: 'background',
      progressTitle: 'Personalize',
      title: 'Determining your price and policy options',
      component: (
        <Steps.Background
          formState={formState.pages.background}
          healthProfileFormState={formState.pages.myHealthProfile}
          basicInfo={basicInfo}
          onFieldChange={(field, value) => onFieldChange('background', field, value)}
          setFormState={(coverageAmount, termLength, healthClass) =>
            setFormState({
              ...formState,
              pages: { ...formState.pages, quoter: { ...formState.pages.quoter, coverageAmount: coverageAmount, term: termLength, healthClass: healthClass } },
            })
          }
          onNext={onNext}
          onBack={onBack}
          resetPersonalizeOptions={resetPersonalizeOptions}
          coverageAmountSpecs={termData.specs.coverageAmount}
        />
      ),
      categoryNumber: 3,
      categoryStep: 2,
      showContainer: true,
    },
    {
      step: 5,
      path: 'tobacco-use',
      progressTitle: 'Tobacco Use',
      title: 'Determining your price and policy options',
      component: (
        <Steps.TobaccoUse
          formState={formState.pages.tobaccoUse}
          basicInfo={basicInfo}
          onFieldChange={(field, value) => onFieldChange('tobaccoUse', field, value)}
          onNext={onNext}
          onBack={onBack}
          coverageAmountSpecs={termData.specs.coverageAmount}
        />
      ),
      categoryNumber: 3,
      categoryStep: 3,
      showContainer: true,
    },
    {
      step: 6,
      path: 'medical-history',
      progressTitle: 'Personalize',
      title: 'Determining your price and policy options',
      component: (
        <Steps.MedicalHistory
          formState={formState.pages.medicalHistory}
          onFieldChange={(field, value) => onFieldChange('medicalHistory', field, value)}
          onNext={onNext}
          onBack={onBack}
        />
      ),
      categoryNumber: 3,
      categoryStep: 4,
      showContainer: true,
    },
    {
      step: 7,
      path: 'medical-details',
      progressTitle: 'Personalize',
      title: 'Determining your price and policy options',
      component: (
        <Steps.MedicalDetails
          formState={formState.pages.medicalDetails}
          medicalHistory={formState.pages.medicalHistory}
          onFieldChange={(field, value) => onFieldChange('medicalDetails', field, value)}
          onNext={onNext}
          onBack={onBack}
          onMultiFieldChange={(fs) => {
            setFormState(fs);
          }}
          fullFormState={formState}
        />
      ),
      categoryNumber: 3,
      categoryStep: 5,
      showContainer: true,
    },
    {
      step: 8,
      path: 'family-history',
      progressTitle: 'Personalize',
      title: 'Determining your price and policy options',
      component: (
        <Steps.FamilyHistory
          formState={formState.pages.familyHistory}
          onFieldChange={(field, value) => onFieldChange('familyHistory', field, value)}
          onNext={onNext}
          onBack={onBack}
        />
      ),
      categoryNumber: 3,
      categoryStep: 6,
      showContainer: true,
    },
    {
      step: 9,
      path: 'alcohol-drug-abuse',
      progressTitle: 'Personalize',
      title: 'Determining your price and policy options',
      component: (
        <Steps.AlcoholDrugAbuse
          formState={formState.pages.alcoholDrugAbuse}
          onFieldChange={(field, value) => onFieldChange('alcoholDrugAbuse', field, value)}
          onNext={onNext}
          onBack={onBack}
        />
      ),
      categoryNumber: 3,
      categoryStep: 6,
      showContainer: true,
    },
    {
      step: 10,
      path: 'driving-record',
      progressTitle: 'Personalize',
      title: 'Determining your price and policy options',
      component: (
        <Steps.DrivingRecord
          formState={formState.pages.drivingRecord}
          onFieldChange={(field, value) => onFieldChange('drivingRecord', field, value)}
          onNext={onNext}
          onBack={onBack}
        />
      ),
      categoryNumber: 3,
      categoryStep: 8,
      showContainer: true,
    },
    {
      step: 11,
      path: 'adventures',
      progressTitle: 'Personalize',
      title: 'Determining your price and policy options',
      component: (
        <Steps.Adventures
          formState={formState.pages.adventures}
          onFieldChange={(field, value) => onFieldChange('adventures', field, value)}
          onNext={onNext}
          onBack={onBack}
        />
      ),
      categoryNumber: 3,
      categoryStep: 9,
      showContainer: true,
    },
    {
      step: 12,
      path: 'get-a-quote',
      progressTitle: 'Get a Quote',
      component: (
        <Steps.Quoter
          formState={formState.pages.quoter}
          formData={formState.pages}
          basicInfo={basicInfo}
          fullFormState={formState}
          onFieldChange={(field, value, _step) => onFieldChange(_step || 'quoter', field, value)}
          setFormState={(coverageAmount, termLength, healthClass) =>
            setFormState({
              ...formState,
              pages: { ...formState.pages, quoter: { ...formState.pages.quoter, coverageAmount: coverageAmount, term: termLength, healthClass: healthClass } },
            })
          }
          onNext={onNext}
          onBack={onBack}
          onNextLga={onNextLga}
          onMultiFieldChange={(fs) => {
            sessionStorage.setItem(SESSION_STORAGE_COVR_FULLUW_KEY, JSON.stringify(fs));
            setFormState(fs);
          }}
          coverageAmountSpecs={termData.specs.coverageAmount}
          quoteLogId={quoteLogId}
          multiTermResult={multiTermResult}
          setQuoteLogId={setQuoteLogId}
        />
      ),
      hideProgressTracker: true,
      showContainer: false,
    },
    {
      step: 13,
      path: 'schedule-appointment',
      progressTitle: 'Schedule your application',
      component: (
        <Steps.Appointment
          formState={formState.pages.appointment}
          quoter={formState.pages.quoter}
          basicInfo={basicInfo}
          onFieldChange={(field, value) => onFieldChange('appointment', field, value)}
          onNext={onNext}
          onBack={onBack}
          product={termData.name}
          isAppointmentSchedulingEnabled={mctProductSettings ? mctProductSettings.enableAppointmentScheduling : true}
          baseURL={'/quote/term-life-insurance/multi-carrier-term/schedule-appointment'}
          setMultiField={(firstName, lastName, email) => {
            setFormState({ ...formState, pages: { ...formState.pages, appointment: { ...formState.pages.appointment, firstName: firstName, lastName: lastName, email: email } } });
            sessionStorage.setItem(SESSION_STORAGE_COVR_FULLUW_KEY, JSON.stringify(formState));
          }}
          fullFormState={formState}
          onMultiFieldChange={(fs) => {setFormState(fs);}}
        />
      ),
      hideProgressTracker: true,
      headerText: 'Schedule your application',
      hideMainHeader: false,
      whiteBackground: true,
      showContainer: true,
      hidden: true,
      fullWidth: true,
    },
    {
      step: 14,
      path: 'thank-you',
      progressTitle: 'ThankYou',
      component: <Steps.ThankYou
        sessionStorageKey={SESSION_STORAGE_COVR_FULLUW_KEY}
        name={formState.pages.appointment.firstName}
        date={formState.pages.appointment.specificApptTime}
        basicInfo={basicInfo}
      />,
      hideProgressTracker: true,
      hideMainHeader: false,
      showContainer: false,
      hidden: false,
    },
    {
      step: 15,
      path: 'lga',
      title: 'Your Life Insurance Request',
      progressTitle: 'Getting Started',
      component: <Steps.Lga
        formData={formState.pages}
        basicInfo={basicInfo}
        onBack={() => {navigate(`/quote/term-life-insurance/multi-carrier-term/get-a-quote`);}}
        logQuote={logFinalDigitalQuote}
      />,
      hideProgressTracker: true,
      hidden: false,
  },
  ];

  const currentStepObj = steps.find(s => s.step === formState.currentStep) || steps[0];
  const categories = steps.filter((o) => o.categoryNumber === currentStepObj.categoryNumber);
  const totalCategoriesStep = Math.max(...categories.map((o) => { return o.categoryStep; }));
  const percent = calculatePercentage(3, currentStepObj.categoryNumber, currentStepObj.categoryStep, totalCategoriesStep);

  return (
    <div>
      <Helmet>
        <title>{`Term life insurance - ${currentStepObj?.progressTitle}`}</title>
      </Helmet>

      <HeaderProgressTracker title={currentStepObj.title} hidden={currentStepObj.hidden} showContainer={currentStepObj.showContainer} hideProgressTracker={currentStepObj.hideProgressTracker} headerProductsText={true} stepName={currentStepObj.progressTitle} step={currentStepObj.categoryNumber} totalSteps={3} whiteBg={currentStepObj.whiteBackground} percent={percent} fullWidth={currentStepObj.fullWidth}>
        <div className="MCT">
          {currentStepObj.component}
        </div>
      </HeaderProgressTracker>
    </div>
  );
}
